import React, { useMemo, useState } from "react";
import {
  Alert, Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Drawer, IconButton, MenuItem, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SortIcon from "@mui/icons-material/Sort";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import {
  useGetProgramTargetQuery, useCreateProgramTargetMutation,
  useReorderProgramTargetsMutation, useUpdateTargetStatusMutation,
  useUpdateTargetSDMutation, useDeleteProgramTargetMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { label, TARGET_STATUS_LABELS, MEASUREMENT_LABELS } from "./labels";
import { MEASUREMENT_TYPE_OPTIONS, pickerValueFromRow, measurementTypeLabel } from "../../SessionData/shared/measurementTypeOptions";
import type { MeasurementType } from "../../SessionData/shared/measurementType";
import ImportTargetsDialog from "./ImportTargetsDialog";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

/**
 * Batch 43 — Inline Targets workspace for the Program editor.
 *
 * Loads/creates/updates Targets against a saved Program. The caller passes
 * a `programId`; if null, the workspace shows a friendly hint asking the
 * user to save a Draft first — the parent editor uses the "onNeedDraft"
 * callback to transparently create the Draft before opening this step.
 */
type TargetRow = any;

const TARGET_TYPES = [
  "Single Response", "Multiple Exemplar", "Task Analysis", "Behaviour Reduction",
  "Frequency", "Duration", "Latency", "Interval", "Fluency",
];

// Batch 15 Step 3c — local MEASUREMENT_TYPES retired. Options come
// from the shared source at SessionData/shared/measurementTypeOptions
// so this picker and ProgramLibrary/Create's picker cannot drift.

const STARTING_PHASES = ["waiting", "baseline", "acquisition", "generalization"];

const statusChipColor = (s: string): any => {
  const v = (s || "").toLowerCase();
  if (v === "mastered" || v === "maintenance") return "success";
  if (v === "acquisition" || v === "generalization") return "primary";
  if (v === "baseline" || v === "waiting" || v === "draft") return "warning";
  if (v === "on_hold" || v === "hold" || v === "closed") return "default";
  if (v === "discontinued" || v === "archived") return "default";
  return "default";
};

export default function TargetsWorkspace({
  programId, onNeedDraft,
}: {
  programId: number | string | null;
  onNeedDraft?: () => Promise<number | null>;
}) {
  const { showMessage, showErrorMessage } = useNotifier();
  const { data, refetch, isFetching } = useGetProgramTargetQuery(
    programId ? { programId } : { programId: 0 },
    { skip: !programId }
  );
  const [createTarget, { isLoading: saving }] = useCreateProgramTargetMutation();
  const [reorder] = useReorderProgramTargetsMutation();
  const [updateStatus] = useUpdateTargetStatusMutation();
  const [updateSD] = useUpdateTargetSDMutation();
  const [removeTarget] = useDeleteProgramTargetMutation();

  const targets: TargetRow[] = useMemo(
    () => (Array.isArray(data?.data) ? (data!.data as TargetRow[]) : []),
    [data]
  );

  const [drawer, setDrawer] = useState<{ open: boolean; row: TargetRow | null }>({ open: false, row: null });
  const [confirmDelete, setConfirmDelete] = useState<TargetRow | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  // Batch 43 §3 — sort control (manual order is the source-of-truth
  // ordering; name and status are read-only reorderings for browsing).
  const [sortKey, setSortKey] = useState<"manual" | "name" | "status">("manual");
  // Batch 43 §3 — selection state for the Add SD toolbar action. Persist
  // across sort changes (selection is by id, not row index).
  const [selectedIds, setSelectedIds] = useState<Set<number | string>>(new Set());
  const [sdOpen, setSdOpen] = useState(false);
  const [sdType, setSdType] = useState("");
  const [sdText, setSdText] = useState("");

  const openAdd = async () => {
    if (!programId && onNeedDraft) {
      const id = await onNeedDraft();
      if (!id) { showErrorMessage("Please save the Program first."); return; }
    }
    setDrawer({ open: true, row: null });
  };
  const openEdit = (row: TargetRow) => setDrawer({ open: true, row });

  const move = async (row: TargetRow, delta: number) => {
    if (!programId) return;
    const idx = targets.findIndex((t) => t.id === row.id);
    const swap = targets[idx + delta];
    if (!swap) return;
    const ids = [...targets.map((t) => t.id)];
    [ids[idx], ids[idx + delta]] = [ids[idx + delta], ids[idx]];
    await reorder({ programId, order: ids }).unwrap().catch(() => {});
    refetch();
  };

  const changeStatus = async (row: TargetRow, status: string) => {
    await updateStatus({ id: row.id, status }).unwrap().catch(() => {});
    refetch();
  };

  const doDelete = async (row: TargetRow) => {
    try {
      const r: any = await removeTarget(row.id).unwrap();
      if (r?.status) { showMessage("Target removed."); refetch(); }
      else showErrorMessage(r?.message || "Delete failed.");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Delete failed."); }
    setConfirmDelete(null);
  };

  // Batch 43 §3 — visible ordering derives from sortKey. Manual keeps the
  // server order (the reorder mutation is the only thing that changes it).
  // Name / status are pure browsing reorderings — they do not persist.
  const visibleTargets = useMemo(() => {
    if (sortKey === "manual") return targets;
    const arr = [...targets];
    if (sortKey === "name") {
      arr.sort((a, b) => String(a.targetName ?? "").localeCompare(String(b.targetName ?? "")));
    } else {
      arr.sort((a, b) => String(a.status ?? "").localeCompare(String(b.status ?? ""))
        || String(a.targetName ?? "").localeCompare(String(b.targetName ?? "")));
    }
    return arr;
  }, [targets, sortKey]);

  const toggleSelect = (id: number | string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === visibleTargets.length) return new Set();
      return new Set(visibleTargets.map((t) => t.id));
    });
  };
  const openSdDialog = () => {
    setSdType("");
    setSdText("");
    setSdOpen(true);
  };
  const applySd = async () => {
    if (selectedIds.size === 0) return;
    try {
      const r: any = await updateSD({
        targetIds: Array.from(selectedIds),
        sdType: sdType || null,
        sdText: sdText || null,
      }).unwrap();
      if (r?.status) {
        showMessage("Discriminative stimulus applied.");
        setSdOpen(false);
        setSelectedIds(new Set());
        refetch();
      } else {
        showErrorMessage(r?.message || "Failed to update SD.");
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to update SD.");
    }
  };

  const summary = useMemo(() => {
    const by = (k: string) => targets.filter((t) => String(t.status || "").toLowerCase() === k).length;
    return {
      total: targets.length,
      active: targets.filter((t) => ["acquisition", "generalization", "maintenance"].includes(String(t.status || "").toLowerCase())).length,
      baseline: by("baseline"), acquisition: by("acquisition"),
      generalization: by("generalization"), mastered: by("mastered"), maintenance: by("maintenance"),
    };
  }, [targets]);

  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }} justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>Targets</Typography>
          <Typography variant="caption" color="text.secondary">
            Define the measurable skills or behaviours Staff will record during ABA Sessions.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {/* Batch 43 §3 — sort control: manual order (server), name, status.
             Skips "Suggest" per spec: that is an AI feature we are not
             building. */}
          <TextField
            select size="small" label="Sort"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as any)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ notched: true }}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="manual"><SortIcon fontSize="small" sx={{ mr: 1, verticalAlign: "middle" }} />Manual order</MenuItem>
            <MenuItem value="name">Name (A→Z)</MenuItem>
            <MenuItem value="status">Status</MenuItem>
          </TextField>
          <Button startIcon={<CloudUploadIcon />} variant="outlined" onClick={() => setImportOpen(true)} disabled={!programId}>Import targets</Button>
          {/* Batch 43 §3 — Add SD applies the same sdType/sdText to every
             selected target via /program-target/update-sd. Disabled until
             at least one row is checked; the count is surfaced on the
             button so it's obvious what "current selection" means. */}
          <Button startIcon={<CampaignOutlinedIcon />} variant="outlined"
            onClick={openSdDialog}
            disabled={!programId || selectedIds.size === 0}
          >Add SD{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}</Button>
          <Button startIcon={<AddIcon />} variant="contained" onClick={openAdd}>Add Target</Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
        <Chip size="small" variant="outlined" label={`Total ${summary.total}`} />
        <Chip size="small" variant="outlined" color="primary" label={`Active ${summary.active}`} />
        <Chip size="small" variant="outlined" label={`Baseline ${summary.baseline}`} />
        <Chip size="small" variant="outlined" label={`Acquisition ${summary.acquisition}`} />
        <Chip size="small" variant="outlined" label={`Generalization ${summary.generalization}`} />
        <Chip size="small" variant="outlined" color="success" label={`Mastered ${summary.mastered}`} />
        <Chip size="small" variant="outlined" label={`Maintenance ${summary.maintenance}`} />
      </Stack>

      <Divider sx={{ my: 2 }} />

      {!programId ? (
        <Alert severity="info">Save the Program as a Draft to unlock the Target editor. The Program will save automatically the first time you open Add Target — nothing is lost.</Alert>
      ) : isFetching ? (
        <Box sx={{ p: 3, textAlign: "center" }}><CircularProgress size={24} /></Box>
      ) : targets.length === 0 ? (
        <Alert severity="info" action={<Button size="small" onClick={openAdd}>Add Target</Button>}>
          No Targets yet. Add the first measurable skill for this Program.
        </Alert>
      ) : (
        <Table size="small" data-testid="targets-table">
          <TableHead>
            <TableRow>
              <TableCell width={40} padding="checkbox">
                <Checkbox
                  size="small"
                  indeterminate={selectedIds.size > 0 && selectedIds.size < visibleTargets.length}
                  checked={visibleTargets.length > 0 && selectedIds.size === visibleTargets.length}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select all targets" }}
                />
              </TableCell>
              <TableCell width={30}>#</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Measurement</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center" sx={{ width: 180, minWidth: 180 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleTargets.map((t, i) => (
              <TableRow key={t.id} hover selected={selectedIds.has(t.id)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedIds.has(t.id)}
                    onChange={() => toggleSelect(t.id)}
                    inputProps={{ "aria-label": `Select ${t.targetName}` }}
                  />
                </TableCell>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{t.targetName}</Typography>
                  {t.description && <Typography variant="caption" color="text.secondary">{t.description}</Typography>}
                </TableCell>
                <TableCell>{t.targetType || "Single Response"}</TableCell>
                <TableCell>{label(MEASUREMENT_LABELS, t.measurementType, "—")}</TableCell>
                <TableCell>
                  <Select size="small" value={(t.status || "waiting").toLowerCase()}
                    onChange={(e) => changeStatus(t, e.target.value)}
                    renderValue={(v: any) => (
                      <Chip size="small" color={statusChipColor(v) as any} label={label(TARGET_STATUS_LABELS, v)} />
                    )}
                    sx={{ minWidth: 150 }}>
                    {Object.entries(TARGET_STATUS_LABELS).map(([k, v]) => (
                      <MenuItem key={k} value={k}>{v}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell align="center" sx={{ px: 1 }}>
                  <Tooltip title={sortKey === "manual" ? "Move up" : "Available in Manual order"}>
                    <span><IconButton size="small" onClick={() => move(t, -1)} disabled={sortKey !== "manual" || i === 0}><ArrowUpwardIcon fontSize="inherit" /></IconButton></span>
                  </Tooltip>
                  <Tooltip title={sortKey === "manual" ? "Move down" : "Available in Manual order"}>
                    <span><IconButton size="small" onClick={() => move(t, 1)} disabled={sortKey !== "manual" || i === targets.length - 1}><ArrowDownwardIcon fontSize="inherit" /></IconButton></span>
                  </Tooltip>
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" onClick={() => setConfirmDelete(t)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <TargetDrawer open={drawer.open} row={drawer.row} programId={programId}
        onClose={() => setDrawer({ open: false, row: null })}
        onSaved={() => { setDrawer({ open: false, row: null }); refetch(); }} />

      {/* Batch 44 — replaced the 5-step ImportTargetsWizard with the
         single-dialog paste flow the client demonstrated. The wizard's
         copy-from-program / copy-from-student / CSV upload options are
         deferred: paste import is the actual workflow they use. */}
      <ImportTargetsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        programId={programId}
        existingNames={targets.map((t) => String(t.targetName ?? ""))}
        onImported={() => refetch()}
      />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove Target</DialogTitle>
        <DialogContent>Remove <strong>{confirmDelete?.targetName}</strong> from this Program?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => confirmDelete && doDelete(confirmDelete)}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* Batch 43 §3 — Add SD dialog. Writes sdType + sdText to every
         checked target row in one PATCH. Overwrites existing SD on those
         rows; leaving both fields blank clears the SD. */}
      <Dialog open={sdOpen} onClose={() => setSdOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Discriminative Stimulus</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
            Applied to {selectedIds.size} selected target{selectedIds.size === 1 ? "" : "s"}. Overwrites any existing SD on those rows.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="SD Type" size="small" fullWidth
              value={sdType}
              onChange={(e) => setSdType(e.target.value)}
              placeholder="e.g. Verbal, Visual, Written"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="SD / Instruction" size="small" fullWidth multiline minRows={3}
              value={sdText}
              onChange={(e) => setSdText(e.target.value)}
              placeholder='e.g. "What is this?" while holding up the card'
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSdOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={applySd}>Apply</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

function TargetDrawer({ open, row, programId, onClose, onSaved }: {
  open: boolean; row: any; programId: number | string | null;
  onClose: () => void; onSaved: () => void;
}) {
  const { showMessage, showErrorMessage } = useNotifier();
  const [save, { isLoading }] = useCreateProgramTargetMutation();
  const [targetName, setTargetName] = useState("");
  const [description, setDescription] = useState("");
  const [targetType, setTargetType] = useState("Single Response");
  // Batch 15 Step 3c — enum key (or empty string = "inherit from program").
  const [measurementType, setMeasurementType] = useState<MeasurementType | "">("");
  const [startingPhase, setStartingPhase] = useState("waiting");
  const [status, setStatus] = useState("waiting");
  const [sdType, setSdType] = useState("");
  const [sdText, setSdText] = useState("");

  React.useEffect(() => {
    if (open) {
      setTargetName(row?.targetName || "");
      setDescription(row?.description || "");
      setTargetType(row?.targetType || "Single Response");
      setMeasurementType(pickerValueFromRow({
        measurement_type: (row as any)?.measurement_type ?? null,
        measurementType: row?.measurementType ?? null,
      }));
      setStartingPhase(row?.startingPhase || row?.status || "waiting");
      setStatus(row?.status || "waiting");
      setSdType(row?.sdType || "");
      setSdText(row?.sdText || "");
    }
  }, [open, row]);

  const submit = async () => {
    if (!programId) { showErrorMessage("Save the Program first."); return; }
    if (!targetName.trim()) { showErrorMessage("Target name is required."); return; }
    try {
      const r: any = await save({
        id: row?.id, programId,
        targetName: targetName.trim(), description, status,
        sdType, sdText,
        // Batch 15 Step 3c — persist the measurement override. Empty
        // string = "inherit from program" → send null so the
        // controller's reconciler leaves both columns NULL.
        // Pre-existing bug this uncovers: TargetsWorkspace's picker
        // captured measurementType into local state but never sent it
        // in the payload, so no target override was ever saved through
        // this dialog. Now it is.
        measurement_type: measurementType || null,
      } as any).unwrap();
      if (r?.status) { showMessage(row ? "Target updated." : "Target added."); onSaved(); }
      else showErrorMessage(r?.message || "Save failed.");
    } catch (e: any) { showErrorMessage(e?.data?.message || "Save failed."); }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 520 } } }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" fontWeight={700}>{row ? "Edit Target" : "Add Target"}</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Box sx={{ p: 2, overflowY: "auto" }}>
        <Stack spacing={1.5}>
          <TextField size="small" fullWidth required label="Target Name" value={targetName} onChange={(e) => setTargetName(e.target.value)} />
          <TextField size="small" fullWidth multiline minRows={2} label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Target Type</Typography>
              <Select size="small" fullWidth value={targetType} onChange={(e) => setTargetType(e.target.value)}>
                {TARGET_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Measurement Type</Typography>
              <Select size="small" fullWidth
                value={measurementType}
                onChange={(e) => setMeasurementType(e.target.value as MeasurementType | "")}>
                <MenuItem value=""><em>Inherit from program</em></MenuItem>
                {MEASUREMENT_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </Box>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Starting Phase</Typography>
              <Select size="small" fullWidth value={startingPhase} onChange={(e) => setStartingPhase(e.target.value)}>
                {STARTING_PHASES.map((s) => <MenuItem key={s} value={s}>{label(TARGET_STATUS_LABELS, s)}</MenuItem>)}
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Select size="small" fullWidth value={status} onChange={(e) => setStatus(e.target.value)}>
                {Object.entries(TARGET_STATUS_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </Box>
          </Stack>
          <TextField size="small" fullWidth label="SD Type" value={sdType} onChange={(e) => setSdType(e.target.value)} />
          <TextField size="small" fullWidth multiline minRows={2} label="SD / Instruction" value={sdText} onChange={(e) => setSdText(e.target.value)} />
        </Stack>
      </Box>
      <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={isLoading || !targetName.trim()}>
          {isLoading ? "Saving…" : row ? "Save Changes" : "Add Target"}
        </Button>
      </Box>
    </Drawer>
  );
}
